import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { ChartTypes, LineChart } from '@givebutter/primitives/chart';
import { DatePicker } from '@givebutter/primitives/date-picker';
import * as Popover from '@givebutter/primitives/popover';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { faBarsFilter, faCalendar, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchChartStats } from 'api/onboarding';
import {
    ComparisonType,
    DateOption,
    DateOptions,
    DateOptionsDateRange,
} from 'components/account-overview-chart/types';
import * as DropdownMenu from 'components/dropdown-menu';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import { FilterRequestValue } from 'components/filter/types';
import { TransactionFilterRequest } from 'components/filter/types-legacy';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { useIsPending } from 'customHooks/useIsPending';
import { dateFromServer } from 'helpers/dates';
import { transformCurrencyToCompactNumberFormat, truncateText } from 'helpers/formatting';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';
import { OutboundFilters } from 'types/request';

interface ReportAPIResponse {
    interval: string;
    aggregates_by_date_range: {
        average_donation: ReportAPIAggregateByDateRange;
        donation_volume: ReportAPIAggregateByDateRange;
        supporter_count: ReportAPIAggregateByDateRange;
        transaction_count: ReportAPIAggregateByDateRange;
    };
    aggregates_by_interval: Array<{
        amount: number;
        count: number;
        start_date: string;
        end_date: string;
    }>;
}

interface ReportAPIAggregateByDateRange {
    value: number;
    percentage_change: number;
}

const calculateCustomInterval = (date_range: DateOptionsDateRange) => {
    const start = DateTime.fromISO(date_range.start_date);
    const end = DateTime.fromISO(date_range.end_date);
    const diff = end.diff(start, 'days').toObject().days;

    if (diff === undefined || diff > 730) {
        return 'yearly';
    }

    if (diff > 70) {
        return 'monthly';
    }

    if (diff > 1) {
        return 'daily';
    }

    return 'hourly';
};

const DISPLAY_DATE_FORMAT = 'MMM d';
const DISPLAY_DATE_FORMAT_CUSTOM = 'MMM dd, yyyy';

const AccountOverviewChart = () => {
    const history = useHistory();
    const { accountId, firstInclusiveTransactionAt } = useCurrentAccount();
    const updateFilterByKey = useFiltersCache(state => state.updateFiltersNextByKey);

    const dateOptions: DateOptions = {
        [DateOption.Today]: {
            interval: 'hourly',
            buttonLabel: 'Today',
            title: {
                current: "Today's",
                past: 'yesterday',
            },
            start_date: DateTime.now().startOf('day').toISO(),
            end_date: DateTime.now().plus({ hours: 1 }).toISO(),
            comparison_type: ComparisonType.Today,
        },
        [DateOption.Last7Days]: {
            interval: 'daily',
            buttonLabel: 'Last 7 Days',
            title: {
                current: 'Last 7 Days',
                past: 'prior 7 days',
            },
            start_date: DateTime.now().minus({ days: 7 }).toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.Rolling,
        },
        [DateOption.Last4Weeks]: {
            interval: 'daily',
            buttonLabel: 'Last 4 Weeks',
            title: {
                current: 'Last 4 Weeks',
                past: 'prior 4 Weeks',
            },
            start_date: DateTime.now().minus({ weeks: 4 }).toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.Rolling,
        },
        [DateOption.Last3Months]: {
            interval: 'weekly',
            buttonLabel: 'Last 3 Months',
            title: {
                current: 'Last 3 Months',
                past: 'prior 3 Months',
            },
            start_date: DateTime.now().minus({ months: 3 }).toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.Rolling,
        },
        [DateOption.Last12Months]: {
            interval: 'monthly',
            buttonLabel: 'Last 12 Months',
            title: {
                current: 'Last 12 Months',
                past: 'prior 12 Months',
            },
            start_date: DateTime.now().minus({ months: 12 }).toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.Rolling,
        },
        [DateOption.MonthToDate]: {
            interval: 'daily',
            buttonLabel: 'Month to Date',
            title: {
                current: 'Month to Date',
                past: 'same period last month',
            },
            start_date: DateTime.now().startOf('month').toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.MonthToDate,
        },
        [DateOption.QuarterToDate]: {
            interval: 'weekly',
            buttonLabel: 'Quarter to Date',
            title: {
                current: 'Quarter to Date',
                past: 'same period last quarter',
            },
            start_date: DateTime.now().startOf('quarter').toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.QuarterToDate,
        },
        [DateOption.YearToDate]: {
            interval: 'monthly',
            buttonLabel: 'Year to Date',
            title: {
                current: 'Year to Date',
                past: 'same period last year',
            },
            start_date: DateTime.now().startOf('year').toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.YearToDate,
        },
        [DateOption.AllTime]: {
            interval: 'yearly',
            buttonLabel: 'All time',
            title: {
                current: 'All Time',
                past: '--',
            },
            start_date: firstInclusiveTransactionAt
                ? dateFromServer(firstInclusiveTransactionAt).toISO()
                : DateTime.fromISO('2016-01-01T00:00:00').toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.Rolling,
        },
        [DateOption.CustomDate]: {
            interval: 'custom',
            buttonLabel: 'Custom Date',
            title: {
                current: 'Custom Date',
                past: 'previous period',
            },
            start_date: DateTime.fromMillis(0).toISO(),
            end_date: DateTime.now().toISO(),
            comparison_type: ComparisonType.Rolling,
        },
    };

    const { data: campaignData, ref } = useInfiniteCampaigns();
    const campaigns = campaignData?.pages;

    const {
        setChartParams,
        selectedDateOption: selectedDateOptionInternal,
        customDate: customDateInternal,
        selectedCampaignIds: selectedCampaignIdsInternal,
    } = useLocalStorage(state => {
        return {
            setChartParams: state.setChartParams,
            selectedDateOption: state.selectedDateOption,
            customDate: state.customDate,
            selectedCampaignIds: state.selectedCampaignIds,
        };
    });
    const [isPending, { selectedDateOption, customDate, selectedCampaignIds }] = useIsPending(
        {
            selectedDateOption: selectedDateOptionInternal,
            customDate: customDateInternal,
            selectedCampaignIds: selectedCampaignIdsInternal,
        },
        500,
    );

    const selectedCampaignId =
        selectedCampaignIds[accountId] !== undefined &&
        campaigns?.find(camp => camp.id === selectedCampaignIds[accountId]) !== undefined
            ? selectedCampaignIds[accountId]
            : 0;

    const getInterval = () => {
        if (selectedDateOption === DateOption.CustomDate) {
            return calculateCustomInterval({
                start_date: customDate.start_date,
                end_date: customDate.end_date,
            });
        } else if (
            [DateOption.QuarterToDate, DateOption.YearToDate, DateOption.AllTime].includes(
                selectedDateOption,
            )
        ) {
            return calculateCustomInterval({
                start_date: dateOptions[selectedDateOption].start_date,
                end_date: dateOptions[selectedDateOption].end_date,
            });
        }
        return dateOptions[selectedDateOption].interval;
    };

    const {
        data = {
            aggregates_by_date_range: {
                average_donation: { value: 0, percentage_change: 0 },
                donation_volume: { value: 0, percentage_change: 0 },
                supporter_count: { value: 0, percentage_change: 0 },
                transaction_count: { value: 0, percentage_change: 0 },
            },
            chartData: [],
        },
    } = useQuery({
        queryKey: accountQueryKeys.onboardingChartsFilters(
            accountId,
            selectedCampaignId,
            selectedDateOption,
            customDate,
        ),
        queryFn: () => {
            const interval = getInterval();
            let dates: DateOptionsDateRange = {
                start_date: dateOptions[selectedDateOption].start_date,
                end_date: dateOptions[selectedDateOption].end_date,
            };
            if (selectedDateOption === DateOption.CustomDate) {
                dates = {
                    start_date: DateTime.fromISO(customDate.start_date).startOf('day').toISO(),
                    end_date: DateTime.fromISO(customDate.end_date).endOf('day').toISO(),
                };
            }

            return fetchChartStats(
                accountId,
                selectedCampaignId,
                interval,
                dates,
                dateOptions[selectedDateOption].comparison_type,
            );
        },
        select: (
            data: ReportAPIResponse,
        ): {
            aggregates_by_date_range: ReportAPIResponse['aggregates_by_date_range'];
            chartData: ChartTypes.ChartData<{ date: string; Amount: number }>;
        } => ({
            chartData: data.aggregates_by_interval.map(item => {
                let date = '';
                const interval = getInterval();
                switch (interval) {
                    case 'hourly':
                        date = `${DateTime.fromSQL(item.start_date).toLocaleString(
                            DateTime.TIME_SIMPLE,
                        )}`;
                        break;
                    default:
                        date = `${DateTime.fromSQL(item.start_date).toLocaleString(
                            DateTime.DATE_MED,
                        )}`;
                }

                return {
                    date,
                    Amount: item.amount,
                };
            }),
            aggregates_by_date_range: data.aggregates_by_date_range,
        }),
    });

    const { aggregates_by_date_range, chartData } = data;

    const renderDateIntervalLabel = () => {
        if (
            selectedDateOption === DateOption.CustomDate &&
            customDate.start_date !== '' &&
            customDate.end_date !== ''
        ) {
            return `${DateTime.fromISO(customDate.start_date).toFormat(
                DISPLAY_DATE_FORMAT_CUSTOM,
            )} - ${DateTime.fromISO(customDate.end_date).toFormat(DISPLAY_DATE_FORMAT_CUSTOM)}`;
        }

        return dateOptions[selectedDateOption].title.current;
    };

    const handleViewTransactionsClick = () => {
        let start_date = DateTime.fromISO(dateOptions[selectedDateOption].start_date).toISODate();
        let end_date = DateTime.fromISO(dateOptions[selectedDateOption].end_date).toISODate();

        if (selectedDateOption === DateOption.CustomDate) {
            start_date = DateTime.fromISO(customDate.start_date).toISODate();
            end_date = DateTime.fromISO(customDate.end_date).toISODate();
        }

        const campaigns: FilterRequestValue<string[]>[] | undefined =
            selectedCampaignId === 0
                ? undefined
                : [
                      {
                          type: 'and',
                          value: [selectedCampaignId.toString()],
                          operator: '=',
                      },
                  ];

        const values: OutboundFilters<TransactionFilterRequest> = [
            {
                type: 'or',
                children: {
                    campaigns: campaigns,
                    created_at: [
                        {
                            value: [start_date, end_date],
                            type: 'and',
                            operator: 'between',
                        },
                    ],
                },
            },
        ];

        updateFilterByKey('transactions', transformSavedFilterGroups(values));
        history.push(`/accounts/${accountId}/transactions`);
    };

    return (
        <div className={'flex w-full flex-col gap-2'}>
            <div className="flex justify-between">
                <div className="flex h-fit items-center rounded-md border border-gray-300">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <Button
                                variant="solid"
                                color="neutral"
                                size="sm"
                                className="rounded-r-none border-r border-gray-300 bg-white"
                            >
                                {dateOptions[selectedDateOption].buttonLabel}&nbsp;
                                <FontAwesomeIcon icon={faChevronDown} />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content align={'start'} sideOffset={4}>
                            {Object.values(DateOption)
                                .filter(val => val !== DateOption.CustomDate)
                                .map(val => (
                                    <DropdownMenu.Item
                                        key={val}
                                        onClick={() => {
                                            setChartParams(val, customDate, {
                                                ...selectedCampaignIds,
                                                [accountId]: selectedCampaignId,
                                            });
                                        }}
                                    >
                                        {dateOptions[val].buttonLabel}
                                    </DropdownMenu.Item>
                                ))}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                    <Popover.Root>
                        <Popover.Trigger asChild={true}>
                            <Button
                                variant="solid"
                                color="neutral"
                                size="sm"
                                className={`rounded-l-none bg-white`}
                                onClick={() => {
                                    setChartParams(DateOption.CustomDate, customDate, {
                                        ...selectedCampaignIds,
                                        [accountId]: selectedCampaignId,
                                    });
                                }}
                            >
                                {selectedDateOption === DateOption.CustomDate ? (
                                    `${DateTime.fromISO(customDate.start_date).toFormat(
                                        DISPLAY_DATE_FORMAT_CUSTOM,
                                    )} - ${DateTime.fromISO(customDate.end_date).toFormat(
                                        DISPLAY_DATE_FORMAT_CUSTOM,
                                    )}`
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCalendar} />
                                        &nbsp;
                                        {`${DateTime.fromISO(
                                            dateOptions[selectedDateOption].start_date,
                                        ).toFormat(DISPLAY_DATE_FORMAT)} - ${DateTime.fromISO(
                                            dateOptions[selectedDateOption].end_date,
                                        ).toFormat(DISPLAY_DATE_FORMAT)}`}
                                    </>
                                )}
                            </Button>
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content align={'start'} sideOffset={4}>
                                <DatePicker
                                    isTextInput={false}
                                    selectRange={true}
                                    onChange={dates => {
                                        const typedDates = dates as [DateTime, DateTime];
                                        const start_date = typedDates[0].toISODate();
                                        const end_date = typedDates[1].toISODate();
                                        setChartParams(
                                            selectedDateOption,
                                            {
                                                start_date,
                                                end_date,
                                            },
                                            {
                                                ...selectedCampaignIds,
                                                [accountId]: selectedCampaignId,
                                            },
                                        );
                                    }}
                                />
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                </div>
                <div className="relative">
                    <Popover.Root>
                        <Popover.Trigger asChild={true}>
                            <Button variant="outline" color="neutral" size="sm" className="gap-0">
                                <FontAwesomeIcon icon={faBarsFilter} />
                                <span className="hidden sm:inline">&nbsp; Filters</span>
                            </Button>
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content
                                align={'end'}
                                sideOffset={4}
                                className="max-h-64 overflow-y-auto border-gray-100"
                            >
                                <RadioGroup.Root
                                    value={selectedCampaignId.toString()}
                                    onValueChange={value => {
                                        setChartParams(selectedDateOption, customDate, {
                                            ...selectedCampaignIds,
                                            [accountId]: parseInt(value),
                                        });
                                    }}
                                    className="gap-y-0"
                                >
                                    <div className="border-b border-gray-100">
                                        <RadioGroup.ItemContainer className="items-center p-2.5">
                                            <RadioGroup.Item value={'0'} id="allCampaigns">
                                                <RadioGroup.Indicator />
                                            </RadioGroup.Item>
                                            <label
                                                htmlFor="allCampaigns"
                                                className="cursor-pointer"
                                            >
                                                All Campaigns
                                            </label>
                                        </RadioGroup.ItemContainer>
                                    </div>

                                    {campaigns?.map(camp => {
                                        return (
                                            <div key={camp.id} className="border-b border-gray-100">
                                                <RadioGroup.ItemContainer className="items-center p-2.5">
                                                    <RadioGroup.Item
                                                        value={camp.id.toString()}
                                                        id={`option${camp.id}`}
                                                    >
                                                        <RadioGroup.Indicator />
                                                    </RadioGroup.Item>
                                                    <label
                                                        htmlFor={`option${camp.id}`}
                                                        className="cursor-pointer"
                                                    >
                                                        {truncateText(camp.title)}
                                                    </label>
                                                </RadioGroup.ItemContainer>
                                            </div>
                                        );
                                    })}
                                    <div ref={ref} className="h-2">
                                        &nbsp;
                                    </div>
                                </RadioGroup.Root>
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                </div>
            </div>
            <Box className={twJoin(`p-4`, isPending && 'is-loading')}>
                <div className="flex justify-between">
                    <div className="text-sm text-gray-500">
                        {renderDateIntervalLabel()} Donation Volume
                    </div>
                    <div className="fill-gray-500">
                        <DropdownMenu.Root>
                            <DropdownMenu.EllipsisTrigger />
                            <DropdownMenu.Content align={'end'}>
                                <DropdownMenu.Item onClick={handleViewTransactionsClick}>
                                    View Transactions
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-2xl font-medium">
                        ${maskDecimal(aggregates_by_date_range.donation_volume.value)}
                    </span>
                    <span>
                        {selectedDateOption !== DateOption.AllTime && (
                            <>
                                <PercentageChangeLabel
                                    percentage_change={
                                        aggregates_by_date_range.donation_volume.percentage_change
                                    }
                                />
                                &nbsp;
                                <span className="text-xs text-gray-500">
                                    vs {dateOptions[selectedDateOption].title.past}
                                </span>
                            </>
                        )}
                    </span>
                </div>
                <div className="flex items-center justify-end text-xs text-gray-600">
                    <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-blue-500" />
                    {selectedCampaignId === 0
                        ? 'All Campaigns'
                        : campaigns?.filter(camp => {
                              return camp.id === selectedCampaignId;
                          })[0].title}
                </div>
                <LineChart.Root
                    data={chartData}
                    index={'date'}
                    categories={['Amount']}
                    valueFormatter={(value: number) => {
                        if (value >= 1000000) {
                            const formatted = transformCurrencyToCompactNumberFormat(value);
                            return `$${formatted}`;
                        }
                        if (value === 0) {
                            return '$0';
                        } else {
                            return `$${maskDecimal(value)}`;
                        }
                    }}
                    yAxisWidth={48}
                    tickFormatter={(_, rawValue) => {
                        if (rawValue > 999) {
                            return `$${transformCurrencyToCompactNumberFormat(rawValue)}`;
                        }

                        return `$${maskWholeNumber(rawValue)}`;
                    }}
                >
                    <LineChart.Content className={'h-48'}>
                        <LineChart.Tooltip />
                    </LineChart.Content>
                </LineChart.Root>
            </Box>

            <div className="flex flex-col justify-between gap-2 sm:flex-row">
                <div className="w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-sm text-gray-500">
                        {renderDateIntervalLabel()} Supporters
                    </div>
                    <div className="text-2xl font-medium">
                        {maskWholeNumber(aggregates_by_date_range.supporter_count.value)}
                    </div>
                    {selectedDateOption !== DateOption.AllTime && (
                        <div>
                            <PercentageChangeLabel
                                percentage_change={
                                    aggregates_by_date_range.supporter_count.percentage_change
                                }
                            />
                            &nbsp;
                            <span className="text-xs text-gray-500">
                                vs {dateOptions[selectedDateOption].title.past}
                            </span>
                        </div>
                    )}
                </div>

                <div className="w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-sm text-gray-500">
                        {renderDateIntervalLabel()} Transactions
                    </div>
                    <div className="text-2xl font-medium">
                        {maskWholeNumber(aggregates_by_date_range.transaction_count.value)}
                    </div>
                    {selectedDateOption !== DateOption.AllTime && (
                        <div>
                            <PercentageChangeLabel
                                percentage_change={
                                    aggregates_by_date_range.transaction_count.percentage_change
                                }
                            />
                            &nbsp;
                            <span className="text-xs text-gray-500">
                                vs {dateOptions[selectedDateOption].title.past}
                            </span>
                        </div>
                    )}
                </div>

                <div className="w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="text-sm text-gray-500">
                        {renderDateIntervalLabel()} Avg. Donation
                    </div>
                    <div className="text-2xl font-medium">
                        ${maskDecimal(aggregates_by_date_range.average_donation.value)}
                    </div>
                    {selectedDateOption !== DateOption.AllTime && (
                        <div>
                            <PercentageChangeLabel
                                percentage_change={
                                    aggregates_by_date_range.average_donation.percentage_change
                                }
                            />
                            &nbsp;
                            <span className="text-xs text-gray-500">
                                vs {dateOptions[selectedDateOption].title.past}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PercentageChangeLabel = ({
    percentage_change,
}: {
    percentage_change: ReportAPIAggregateByDateRange['percentage_change'];
}) => {
    if (percentage_change < 0) {
        return <span className="text-xs text-red-700">{maskWholeNumber(percentage_change)}%</span>;
    } else {
        return (
            <span className="text-xs text-green-700">+{maskWholeNumber(percentage_change)}%</span>
        );
    }
};

export { AccountOverviewChart, DateOption };
export type { DateOptionsDateRange };
